import { useSyncExternalStore } from 'react';

/**
 * The planner's RUNNING STOPWATCHES, visible to the whole app.
 *
 * The source of truth for elapsed time is the day document in the database
 * (`startAt` + `spent` per task) — that is why a timer survives reloads and
 * devices. This store is the LIVE MIRROR of "something is running right now":
 * PlannerView writes it on every start/pause/load, and the floating chip
 * (rendered at App level, on every screen) reads it, so the clock keeps
 * ticking in front of the user wherever they navigate, until THEY stop it.
 */
export interface RunningTimer {
  day: string; taskId: string; title: string;
  /** epoch ms when ▶ was pressed */ startAt: number;
  /** seconds already banked before this run */ spent: number;
}

const KEY = 'lexidy-planner-running';
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());
function load(): RunningTimer[] {
  try { const r = localStorage.getItem(KEY); return r ? (JSON.parse(r) as RunningTimer[]) : []; } catch { return []; }
}
let cache: RunningTimer[] = load();

export function setTimers(ts: RunningTimer[]): void {
  cache = ts;
  try { if (ts.length) localStorage.setItem(KEY, JSON.stringify(ts)); else localStorage.removeItem(KEY); } catch { /* full */ }
  emit();
}
/** Write only when something actually changed — callers can sync every render. */
export function syncTimers(ts: RunningTimer[]): void {
  if (JSON.stringify(ts) !== JSON.stringify(cache)) setTimers(ts);
}
export function useRunningTimers(): RunningTimer[] {
  return useSyncExternalStore(
    (cb) => { listeners.add(cb); return () => listeners.delete(cb); },
    () => cache,
    () => [],
  );
}

/* ── The floating chip's own preferences ─────────────────────────────
   Per-device mirror (instant) of what the planner's Ajustes stores in the
   user's config document (cross-device). Recurring defaults ON, super
   admins default OFF — the resolver lives in the chip, which receives the
   role-based default from App. */
export type ChipCorner = 'br' | 'bl' | 'tr' | 'tl';
export interface ChipPrefs { mode?: 'on' | 'off'; corner?: ChipCorner; inPlanner?: boolean }
const PREF_KEY = 'lexidy-pk-chip';
function loadPrefs(): ChipPrefs {
  try { return JSON.parse(localStorage.getItem(PREF_KEY) || '{}') as ChipPrefs; } catch { return {}; }
}
/* The snapshot the chip subscribes to. useSyncExternalStore only re-renders
   when the snapshot REFERENCE changes — so writes must replace this object,
   never mutate it (reading localStorage inside the getter would loop forever
   AND never notify). */
let prefsCache: ChipPrefs = loadPrefs();
export function readChipPrefs(): ChipPrefs {
  return prefsCache;
}
export function writeChipPrefs(p: ChipPrefs): void {
  prefsCache = { ...p };
  try { localStorage.setItem(PREF_KEY, JSON.stringify(prefsCache)); } catch { /* full */ }
  emit();
}
/** Reactive prefs for the chip: flipping a switch in the planner's Ajustes
 *  re-renders the bubble INSTANTLY, no reload. */
export function useChipPrefs(): ChipPrefs {
  return useSyncExternalStore(
    (cb) => { listeners.add(cb); return () => listeners.delete(cb); },
    () => prefsCache,
    () => prefsCache,
  );
}

/** 07:42 · 1:07:42 — a real clock, seconds included. */
export const fmtClock = (sec: number): string => {
  const t = Math.max(0, Math.floor(sec));
  const h = Math.floor(t / 3600), m = Math.floor((t % 3600) / 60), s = t % 60;
  const mm = String(m).padStart(2, '0'), ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
};
